import { useEffect, useMemo, useState } from "react";

import {
  Badge,
  Button,
  FontIcon,
  TableColumnDefinition,
  TableRowDefinition,
  TableWithDefaultFilters,
  Typography,
} from "@ezmid/components-library/";
import { faDatabase, faLaptop } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

import { UserCourseLessonModal } from "./UserCourseLessonModal";
import { useGetUserCoursesByUserIdQuery } from "../../../api/user-courses.generated";
import { truncateWithEllipsis } from "../../../utils/stringUtils";

type Props = {
  id: string;
};

type TableData = {
  name: string;
  lecturer: string;
  description: string;
  numberOfLessons: number;
  lessons: string | null;
  createdAt: string;
};

export function UserCoursesCard<T extends object>({ id }: Props) {
  const { t, i18n } = useTranslation();

  const langCode = i18n.language.split("-")[0];
  const { data, isFetching } = useGetUserCoursesByUserIdQuery(
    {
      id: id ?? "",
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const columns: Array<TableColumnDefinition<TableData>> = useMemo(
    () => [
      {
        Header: t("table.header.name"),
        id: "name",
        accessor: "name",
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Typography variant="body1">
            {truncateWithEllipsis(row?.original.name, 30)}
          </Typography>
        ),
        width: "250px",
      },
      {
        Header: t("table.header.lecturer"),
        id: "lecturer",
        accessor: "lecturer",
        width: "200px",
      },
      {
        Header: t("table.header.type.lessonsNumber"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            status="break"
            leftIcon={
              <FontIcon
                marginTop={3}
                customIcon={<FontAwesomeIcon size="lg" icon={faDatabase} />}
              />
            }
          >
            {row?.original.numberOfLessons ?? ""}
          </Badge>
        ),
        id: "numberOfLessons",
        accessor: "numberOfLessons",
        width: "150px",
      },
      {
        Header: t("table.header.lessons"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={() => setCourseId(row?.original.id || "")}
            >
              {t("button.show")}
            </Button>
          </div>
        ),
        id: "lessons",
        accessor: "lessons",
        width: "150px",
      },
    ],
    []
  );

  const memoizedData: Array<TableData> = useMemo(
    () =>
      data?.items?.map(({ course }) => {
        const translatedName = course?.translations?.find(
          (translation) => translation?.language === langCode
        )?.name;
        return {
          name: translatedName || "",
          lecturer: course?.lecturer?.fullName || "",
          numberOfLessons: course?.lessons?.length || 0,
          description: "", // Removed course?.product - property doesn't exist
          createdAt: course?.createdAt || "",
          id: course?.id || "",
          lessons: null,
        };
      }) || [],
    [data]
  );

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [courseId, setCourseId] = useState<string>("");

  useEffect(() => {
    courseId && setIsModalOpen(true);
  }, [courseId]);

  return (
    <>
      <TableWithDefaultFilters
        tableData={memoizedData}
        tableColumns={columns}
        showPagination={false}
        showToolbar={false}
        headerIcon={
          <FontIcon
            marginTop={5}
            customIcon={<FontAwesomeIcon icon={faLaptop} />}
          />
        }
        headerText={t("app.user.courses.information")}
        isLoading={isFetching}
      />
      <UserCourseLessonModal
        onModalClose={() => {
          setIsModalOpen(false);
          setCourseId("");
        }}
        courseId={courseId}
        userId={id}
        isModalOpen={isModalOpen}
      />
    </>
  );
}
